import { useId } from 'react';

import { pt } from '../copy';
import { yearToHumanLabel } from '../lib/prompt';

type TimeSliderProps = {
  years: number[];
  t: number;
  onChange: (t: number) => void;
};

export default function TimeSlider({ years, t, onChange }: TimeSliderProps) {
  const clampedT = Number.isFinite(t) ? Math.min(Math.max(t, 0), 1) : 0;
  const activeIndex = years.length <= 1 ? 0 : Math.round(clampedT * Math.max(0, years.length - 1));
  const activeYear = years[activeIndex];
  const activeLabel = activeYear !== undefined ? yearToHumanLabel(activeYear) : '—';
  const inputId = useId();
  const labelId = useId();
  const descriptionId = useId();

  return (
    <div className="rounded-2xl border border-white/10 bg-black/55 p-4 shadow-2xl shadow-black/30 backdrop-blur-xl">
      <div className="mb-3 flex items-center justify-between gap-3 text-sm text-white/70">
        <span id={labelId}>{pt.timeSliderLabel}</span>
        <span className="shrink-0 rounded-full bg-white/10 px-3 py-1 text-xs uppercase tracking-[0.24em] text-white/85">
          {activeLabel}
        </span>
      </div>

      <input
        aria-describedby={descriptionId}
        aria-labelledby={labelId}
        className="h-2 w-full cursor-pointer accent-white disabled:cursor-default disabled:opacity-40"
        disabled={years.length <= 1}
        id={inputId}
        max={1}
        min={0}
        step={0.001}
        type="range"
        value={clampedT}
        onChange={(event) => onChange(Number(event.target.value))}
      />

      <p className="sr-only" id={descriptionId}>
        {pt.sliderAriaLabel}
      </p>

      <div className="mt-4 flex items-start justify-between gap-2">
        {years.map((year, index) => {
          const isActive = index === activeIndex;
          return (
            <div
              key={`${year}-${index}`}
              className="flex min-w-0 flex-1 flex-col items-center gap-2"
            >
              <span
                className={[
                  'h-2 w-2 rounded-full transition',
                  isActive ? 'bg-white shadow-[0_0_18px_rgba(255,255,255,0.55)]' : 'bg-white/25',
                ].join(' ')}
              />
              <span
                className={[
                  'text-center text-[11px] leading-tight transition',
                  isActive ? 'text-white' : 'text-white/45',
                ].join(' ')}
              >
                {yearToHumanLabel(year)}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}
